import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_MSG =
  "¡Hola! Te recomiendo Mi Ángel en Casa — desde $4.99 USD/mes proteges a tu familia en México con servicio vial, ambulancia, médico y legal 24/7. Si entras con mi link, los dos ganamos 1 mes gratis 🎁";

const MAX_ROWS = 20;

function detectSupport() {
  if (typeof window === "undefined") return { contacts: false, share: false };
  return {
    contacts: !!(navigator.contacts && navigator.contacts.select),
    share: !!navigator.share,
  };
}

export default function InviteFriendsModal({ open, onClose, onSent, referralLink }) {
  const [rows, setRows] = useState([{ nombre: "", email: "", telefono: "" }]);
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [sending, setSending] = useState(false);
  const [support] = useState(detectSupport);

  useEffect(() => {
    if (!open) {
      setRows([{ nombre: "", email: "", telefono: "" }]);
      setMessage(DEFAULT_MSG);
    }
  }, [open]);

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    setRows([...rows, { nombre: "", email: "", telefono: "" }]);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => {
    const copy = [...rows];
    copy[i][field] = value;
    setRows(copy);
  };

  const handlePickContacts = async () => {
    try {
      const picked = await navigator.contacts.select(["name", "email", "tel"], { multiple: true });
      if (!picked || picked.length === 0) return;
      const mapped = picked.map((c) => ({
        nombre: (c.name && c.name[0]) || "",
        email: (c.email && c.email[0]) || "",
        telefono: (c.tel && c.tel[0]) || "",
      })).filter((r) => r.email || r.telefono);
      if (mapped.length === 0) {
        toast.error("Los contactos seleccionados no tienen email");
        return;
      }
      setRows(mapped.slice(0, MAX_ROWS));
      toast.success(`${mapped.length} contactos importados`);
    } catch (e) {
      if (e?.name === "InvalidStateError") {
        toast.error("Permiso de contactos denegado");
      } else {
        toast.error("No se pudo leer los contactos");
      }
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "Mi Ángel en Casa",
        text: message,
        url: referralLink,
      });
      toast.success("Compartido");
      onClose();
    } catch (e) {
      // user cancelled — ignore silently
    }
  };

  const handleSend = async () => {
    const validRows = rows.filter((r) => r.email && r.email.includes("@"));
    if (validRows.length === 0) {
      toast.error("Agrega al menos un email válido");
      return;
    }
    setSending(true);
    try {
      const { data } = await apiClient.post("/referrals/invite", {
        invitees: validRows,
        personal_message: message,
      });
      let msg = `${data.sent} invitación(es) enviada(s)`;
      if (data.skipped > 0) msg += ` · ${data.skipped} omitida(s) (sin email o ya registrados)`;
      toast.success(msg);
      onSent && onSent(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="invite-overlay" data-testid="invite-modal" onClick={(e) => e.target.classList.contains("invite-overlay") && onClose()}>
      <div className="invite-modal">
        <button className="invite-close" onClick={onClose} aria-label="Cerrar" data-testid="invite-close">
          <i className="fas fa-times" />
        </button>

        <h2 className="invite-title">
          <i className="fas fa-user-plus" style={{ color: "#7DC242", marginRight: 10 }} />
          Invita a tus amigos
        </h2>
        <p className="invite-sub">
          Por cada amigo que se registre con tu link, <strong>los dos reciben 1 mes gratis 🎁</strong>
        </p>

        {/* === Quick actions === */}
        <div className="invite-quick">
          {support.contacts && (
            <button onClick={handlePickContacts} className="invite-quick-btn" data-testid="pick-contacts-btn">
              <i className="fas fa-address-book" />
              <div>
                <strong>Elegir de mis contactos</strong>
                <small>Selecciona varios a la vez</small>
              </div>
            </button>
          )}
          {support.share && (
            <button onClick={handleNativeShare} className="invite-quick-btn" data-testid="native-share-btn">
              <i className="fas fa-share-alt" />
              <div>
                <strong>Compartir link</strong>
                <small>WhatsApp, SMS, Email…</small>
              </div>
            </button>
          )}
        </div>

        {!support.contacts && !support.share && (
          <p style={{ background: "#FFFBEB", border: "1px solid #FBBF24", padding: 10, borderRadius: 8, fontSize: 12, color: "#92400E", margin: "8px 0 16px" }}>
            <i className="fas fa-info-circle" /> En este dispositivo no podemos acceder a tus contactos. Agrégalos manualmente abajo 👇
          </p>
        )}

        {/* === Privacy notice === */}
        <p className="invite-privacy">
          🔒 Solo usamos los datos para enviar **una** invitación. No los guardamos ni los compartimos con terceros.
        </p>

        {/* === Manual entries === */}
        <h3 className="invite-section-title">Lista de amigos a invitar</h3>
        <div className="invite-rows" data-testid="invite-rows">
          {rows.map((row, i) => (
            <div key={i} className="invite-row">
              <input
                placeholder="Nombre"
                value={row.nombre}
                onChange={(e) => updateRow(i, "nombre", e.target.value)}
                className="invite-input"
                data-testid={`invite-name-${i}`}
              />
              <input
                type="email"
                placeholder="amigo@email.com *"
                value={row.email}
                onChange={(e) => updateRow(i, "email", e.target.value)}
                className="invite-input"
                data-testid={`invite-email-${i}`}
              />
              <input
                type="tel"
                placeholder="(opcional)"
                value={row.telefono}
                onChange={(e) => updateRow(i, "telefono", e.target.value)}
                className="invite-input invite-input-tel"
                data-testid={`invite-phone-${i}`}
              />
              {rows.length > 1 && (
                <button onClick={() => removeRow(i)} className="invite-remove" aria-label="Quitar" data-testid={`invite-remove-${i}`}>
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          ))}
          {rows.length < MAX_ROWS && (
            <button onClick={addRow} className="invite-add" data-testid="invite-add-btn">
              <i className="fas fa-plus" /> Agregar otro amigo
            </button>
          )}
        </div>

        {/* === Editable message === */}
        <h3 className="invite-section-title">Mensaje personal (opcional)</h3>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={500}
          className="invite-message"
          rows={4}
          data-testid="invite-message"
        />
        <small style={{ color: "#9ca3af", display: "block", textAlign: "right" }}>
          {message.length}/500
        </small>

        <div className="invite-actions">
          <button onClick={onClose} className="btn btn-secondary" data-testid="invite-cancel">Cancelar</button>
          <button
            onClick={handleSend}
            disabled={sending || rows.every((r) => !r.email)}
            className="btn btn-primary"
            data-testid="invite-send"
          >
            {sending ? "Enviando..." : <><i className="fas fa-paper-plane" /> Enviar invitaciones</>}
          </button>
        </div>
      </div>
    </div>
  );
}
